import { FieldExpandableContentBlocks } from '../drupal/blockDefinitions.fields'
import { mediaNestedSrc, mediaNestedSrcSet, mediaOrginalSrc, mediaOrginalSrcSet } from '../drupal/utils/imageSizes'
import { parseDrupalImageUrls } from '../drupal/utils/modernImage'
import { ExpandableItem } from '../modernBlocks/blocks/expandableBlock/expandableBlock.state'
import { MediaOriginalSizeProps } from '../modernBlocks/blocks/mediaBlock/mediaBlock.state'
import { NestedBlock } from '../modernBlocks/modernBlocks.state'
import { linkWrapper, removeLanguageFromLinkStart } from './link'

export function parseExpandable(expandableblock: FieldExpandableContentBlocks): ExpandableItem[] {
  return expandableblock
    ? expandableblock.map((item, index) => {
        const itemAnchor: string = item.target.field_anchor ? item.target.field_anchor[0]?.value.toLowerCase() : ''
        return {
          index,
          isOpen: handleHashOpenExpand(itemAnchor),
          anchor: itemAnchor,
          title: item.target.field_title ? item.target.field_title[0]?.value : '',
          description: item.target.field_subtitle ? item.target.field_subtitle[0]?.value : '',
          body: item.target.field_body ? item.target.field_body[0]?.processed : '',
          nestedBlocks: parseNestedBlocks(item.target.field_content_block),
        }
      })
    : []
}

export const parseNestedBlocks = (input: FieldExpandableContentBlocks[0]['target']['field_content_block']) => {
  return input
    ? input.map((block, index) => {
        const kind: 'text_nested' | 'media_nested' | 'quote_nested' | 'links_list_nested' | 'download_nested' = block
          .target.type[0]?.target_id as any
        switch (kind) {
          case 'text_nested':
            return {
              kind: 'textNested',
              index: `text-quote-${index.toString()}`,
              body: block.target.field_body ? block.target.field_body[0]?.processed : '',
            } as NestedBlock
          case 'media_nested':
            const isImage: boolean = block.target.field_media_item
              ? block.target.field_media_item[0].target.field_media[0].target.bundle[0]?.target_id == 'image'
              : false
            const isVideo: boolean = block.target.field_media_item
              ? block.target.field_media_item[0].target.field_media[0].target.bundle[0]?.target_id == 'video'
              : false
            const fieldMediaImage = isImage
              ? block.target.field_media_item
                ? block.target.field_media_item[0].target.field_media[0]?.target.field_media_image[0]
                : undefined
              : undefined
            const fieldMediaSource = isImage
              ? block.target.field_media_item
                ? block.target.field_media_item[0].target.field_media[0]?.target.field_source[0]
                  ? block.target.field_media_item[0].target.field_media[0]?.target.field_source
                  : undefined
                : undefined
              : undefined
            const originalSizeAttrs: MediaOriginalSizeProps =
              isImage && block.target.field_media_item
                ? {
                    showinOriginalSize: block.target.field_media_item[0]?.target?.field_original_size[0]?.value,
                    width: block.target.field_media_item[0].target.field_media[0]?.target.field_media_image[0]?.width,
                    height: block.target.field_media_item[0].target.field_media[0]?.target.field_media_image[0]?.height,
                  }
                : {
                    showinOriginalSize: false,
                  }
            const dpImages = fieldMediaImage
              ? parseDrupalImageUrls(
                  fieldMediaImage,
                  fieldMediaSource,
                  originalSizeAttrs.showinOriginalSize == true ? mediaOrginalSrcSet : mediaNestedSrcSet,
                  originalSizeAttrs.showinOriginalSize == true ? mediaOrginalSrc : mediaNestedSrc
                )?.image
              : undefined
            return {
              kind: 'mediaNested',
              index: `nested-media-${index.toString()}`,
              type: isImage ? ('image' as 'image') : ('video' as 'video'),
              displayOption: 'content-width',
              originalSize: originalSizeAttrs,
              caption: block.target.field_media_item
                ? block.target.field_media_item[0]?.target?.field_image_caption[0]?.value
                : undefined,
              source: block.target.field_media_item
                ? block.target.field_media_item[0].target?.field_media[0]?.target.field_source[0]?.value
                : undefined,
              url: isVideo
                ? block.target.field_media_item
                  ? (block.target.field_media_item[0].target as any).field_media[0]?.target
                      .field_media_video_embed_field[0]?.value
                  : undefined
                : undefined,
              label: isVideo
                ? block.target.field_media_item
                  ? block.target.field_media_item[0]?.target?.field_media[0]?.target_label
                  : undefined
                : undefined,
              image: isImage ? dpImages : undefined,
            } as NestedBlock
          case 'quote_nested':
            return {
              kind: 'quoteNested',
              index: `nested-quote-${index.toString()}`,
              design: block.target?.field_design ? block.target?.field_design[0]?.value === 'new' ? 'new' : 'default' : 'default',
              text: block.target?.field_text ? block.target?.field_text[0]?.value : '',
              author: block.target?.field_author_name ? block.target?.field_author_name[0]?.value : undefined,
              authorDescription: block.target?.field_author_description
                ? block.target?.field_author_description[0]?.value
                : undefined,
            } as NestedBlock
          case 'links_list_nested':
            let nestedLinks = block.target?.field_links_list
            return {
              kind: 'linkListNested',
              index: `nested-linklist-${index.toString()}`,
              items:
                nestedLinks?.map((linkBlock, i) => ({
                  subTitle: linkBlock.target.field_text ? linkBlock.target.field_text[0]?.value : '',
                  link: linkWrapper({
                    title: linkBlock.target.field_url ? linkBlock.target.field_url[0]?.title : '',
                    url: removeLanguageFromLinkStart(
                      linkBlock.target.field_url[0].processed_url ? linkBlock.target.field_url[0].processed_url : ''
                    ),
                    anchor: linkBlock.target.field_url ? linkBlock.target?.field_url[0]?.options?.attributes?.id : '',
                  }),
                  key: `nested-linklist-${index.toString()}-item-${i}`,
                })) || [],
            } as NestedBlock
          case 'download_nested':
            const item = block.target.field_download && block.target.field_download[0].target
            return {
              kind: 'downloadNested',
              index: `nested-download-${index.toString()}`,
              title: block.target?.field_title ? block.target?.field_title[0]?.value : '',
              intro: block.target?.field_text ? block.target?.field_text[0]?.value : '',
              item: {
                title: item && item.name[0]?.value,
                text: '',
                fileUrl: item && item.field_media_file[0]?.target.uri[0]?.url,
                fileName: item && item.field_media_file[0]?.target.filename[0]?.value,
              },
            } as NestedBlock
          default:
            throw Error
        }
      })
    : []
}

const handleHashOpenExpand = (anchor: string) => {
  if (anchor == '') return false

  if (window.location.hash.substring(1) == anchor) return true

  return false
}
